// ─────────────────────────────────────────────────────────────────────────────
// PICKLE BARREL — Loot system data layer
// Awarded on round win. One barrel per run. Higher level = better rarity odds.
// Barrels now drop Stones and Ingredients only. Baguettes are bred, not found.
// ─────────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────────
// RARITY TIERS
// ─────────────────────────────────────────────────────────────────────────────

export type BarrelRarity = 'fresh' | 'brined' | 'fermented' | 'legendary_dill';

/** Unified rarity tier used across stones, baguettes, and starter doughs. */
export type Rarity = 'common' | 'uncommon' | 'rare' | 'ultra_rare' | 'legendary';

/** Maps BarrelRarity → Rarity for cross-system compatibility. */
export const BARREL_TO_RARITY: Record<BarrelRarity, Rarity> = {
    fresh:          'common',
    brined:         'uncommon',
    fermented:      'rare',
    legendary_dill: 'legendary',
};

export const RARITY_TO_BARREL: Partial<Record<Rarity, BarrelRarity>> = {
    common:   'fresh',
    uncommon: 'brined',
    rare:     'fermented',
    ultra_rare: 'legendary_dill',
    legendary: 'legendary_dill',
};

// ─────────────────────────────────────────────────────────────────────────────
// STONE TYPES
// ─────────────────────────────────────────────────────────────────────────────

export type StoneMode = 'pvp' | 'adventure' | 'both';

export type StoneType =
    // ── Adventure ──
    | 'power'       // crumb cannon damage
    | 'chaos'       // ball damage
    | 'brine'       // shield strength
    | 'crust'       // damage resistance
    // ── Both ──
    | 'dill'        // PvP charge threshold -1–2 hits / Adventure cooldown reduction
    | 'score'       // score multiplier
    | 'ferment'     // XP & Brine Credit gain multiplier
    | 'void'        // truly random effect per match (Ultra Rare+)
    | 'eternal'     // amplifies all other socketed stones (Legendary only)
    // ── PvP ──
    | 'velocity'    // pickle speed
    | 'topspin'     // spin intensity
    | 'charge'      // charge hit power
    | 'reflex'      // paddle reaction speed
    | 'ghost';      // ball invisible 0.25–1.0s randomly

export type BaguetteId = string;

// ─────────────────────────────────────────────────────────────────────────────
// STONE INTERFACE
// ─────────────────────────────────────────────────────────────────────────────

export interface Stone {
    id: string;
    type: StoneType;
    rarity: Rarity;
    /** Rolled at drop time, permanent. Range depends on rarity. */
    value: number;       // percentage boost e.g. 4 = +4%
    label: string;       // e.g. "Velocity Stone +4%"
    /** SVG icon key — resolved via STONE_ICON_KEY, rendered by StoneSVG component */
    iconKey: string;
    color: string;
    mode: StoneMode;
    /** Min rarity required to socket this stone */
    minBaguetteRarity: Rarity;
}

// ─────────────────────────────────────────────────────────────────────────────
// TEMP EMOJI MAPPING (Phase A/B)
// ─────────────────────────────────────────────────────────────────────────────

export const ICON_MAP: Record<string, string> = {
    stone_power:    '💥',
    stone_chaos:    '🎾',
    stone_brine:    '🛡️',
    stone_crust:    '🧱',
    stone_dill:     '🥒',
    stone_score:    '✨',
    stone_ferment:  '🧪',
    stone_void:     '🕳️',
    stone_eternal:  '💎',
    stone_velocity: '⚡',
    stone_topspin:  '🌀',
    stone_charge:   '🔋',
    stone_reflex:   '🧠',
    stone_ghost:    '👻',
    ingredient_pit_dust:       '☁️',
    ingredient_brine_drops:    '💧',
    ingredient_active_culture: '🦠',
    ingredient_salt_stone:      '🧂',
};

// ─────────────────────────────────────────────────────────────────────────────
// BAGUETTE INTERFACE (bred, not dropped)
// ─────────────────────────────────────────────────────────────────────────────

export interface Baguette {
    id: BaguetteId;
    /** Display name e.g. "Pit Culture Baguette" */
    name: string;
    rarity: Rarity;
    /** Realized socket count from breeding — may be less than strain ceiling due to neglect */
    maxSockets: number;
    /** Sockets array — null = empty */
    stones: (Stone | null)[];
    /** Strain the dough came from */
    strainId: StarterStrainId;
    /** Timestamp of bake completion */
    bakedAt: number;
    /**
     * Phase 2: Algorand ASA ID once minted as ARC19 NFT.
     * null = not yet minted, stored in Firebase only.
     */
    asaId: number | null;
    /** true = listed on marketplace, stones are locked with it */
    isListed: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// STARTER DOUGHS
// ─────────────────────────────────────────────────────────────────────────────

export type StarterStrainId =
    | 'pit_culture'         // Common  — 1 socket ceiling, 24h
    | 'brine_wild_yeast'    // Uncommon — 2 socket ceiling, 36h
    | 'fermented_chaos'     // Rare     — 3 socket ceiling, 48h
    | 'ancient_dill_spore'  // Ultra Rare — 4 socket ceiling, 72h
    | 'eternal_crumb';      // Legendary  — 5 socket ceiling, 96h

export interface StarterStrainMeta {
    id: StarterStrainId;
    name: string;
    rarity: Rarity;
    /** Displayed flavor lore */
    lore: string;
    /** Maximum possible sockets if all stages are completed perfectly */
    socketCeiling: number;
    /** Bake duration in milliseconds */
    bakeMs: number;
    /** Gacha drop weight for marketplace pulls */
    gacha_weight: number;
}

export const STARTER_STRAINS: Record<StarterStrainId, StarterStrainMeta> = {
    pit_culture: {
        id: 'pit_culture',
        name: 'Pit Culture',
        rarity: 'common',
        lore: 'Standard-issue LoafPickle-adjacent culture. Reliable. Unremarkable. Approved.',
        socketCeiling: 1,
        bakeMs: 24 * 60 * 60 * 1000,
        gacha_weight: 45,
    },
    brine_wild_yeast: {
        id: 'brine_wild_yeast',
        name: 'Brine Wild Yeast',
        rarity: 'uncommon',
        lore: 'Harvested from the shallows of Brine Lake. It behaves differently on Tuesdays.',
        socketCeiling: 2,
        bakeMs: 36 * 60 * 60 * 1000,
        gacha_weight: 35,
    },
    fermented_chaos: {
        id: 'fermented_chaos',
        name: 'Fermented Chaos Strain',
        rarity: 'rare',
        lore: 'Subject to LoafPickle compliance review. Do not agitate. Do not open near The Pit.',
        socketCeiling: 3,
        bakeMs: 48 * 60 * 60 * 1000,
        gacha_weight: 12,
    },
    ancient_dill_spore: {
        id: 'ancient_dill_spore',
        name: 'Ancient Dill Spore',
        rarity: 'ultra_rare',
        lore: 'Recovered from a barrel that pre-dates the first Pickle Pong championship. Smells incredible.',
        socketCeiling: 4,
        bakeMs: 72 * 60 * 60 * 1000,
        gacha_weight: 7,
    },
    eternal_crumb: {
        id: 'eternal_crumb',
        name: 'The Eternal Crumb',
        rarity: 'legendary',
        lore: 'Nobody knows where it came from. The crumb is not a metaphor. The crumb is real.',
        socketCeiling: 5,
        bakeMs: 96 * 60 * 60 * 1000,
        gacha_weight: 1,
    },
};

export interface StarterDough {
    id: string;
    strainId: StarterStrainId;
    rarity: Rarity;
    /** How acquired: 'gift' | 'gacha' | 'crafted' */
    source: 'gift' | 'gacha' | 'crafted';
    acquiredAt: number;
    /**
     * Phase 2: tradeable flag for when soul-binding is lifted.
     * Always false in Phase 1.
     */
    tradeable: false;
}

// ─────────────────────────────────────────────────────────────────────────────
// INGREDIENTS (Crumb System)
// ─────────────────────────────────────────────────────────────────────────────

export type IngredientType = 'pit_dust' | 'brine_drops' | 'active_culture' | 'salt_stone';

export interface IngredientMeta {
    id: IngredientType;
    name: string;
    lore: string;
    /** Real-world bread analog */
    analog: string;
    /** How it's obtained */
    source: string;
    /** SVG icon key */
    iconKey: string;
    color: string;
}

export const INGREDIENTS: Record<IngredientType, IngredientMeta> = {
    pit_dust: {
        id: 'pit_dust',
        name: 'Pit Dust',
        lore: 'Ground from the floor of the arena. Technically flour. Technically.',
        analog: 'Flour',
        source: 'Baguette recycling',
        iconKey: 'ingredient_pit_dust',
        color: '#e8d5a3',
    },
    brine_drops: {
        id: 'brine_drops',
        name: 'Brine Drops',
        lore: 'Condensation from the walls of Brine Lake. Perfectly pH-balanced for bread.',
        analog: 'Water',
        source: 'Baguette recycling',
        iconKey: 'ingredient_brine_drops',
        color: '#00e5ff',
    },
    active_culture: {
        id: 'active_culture',
        name: 'Active Culture',
        lore: 'It is alive. It has opinions. Feed it.',
        analog: 'Yeast',
        source: 'Baguette recycling',
        iconKey: 'ingredient_active_culture',
        color: '#39ff14',
    },
    salt_stone: {
        id: 'salt_stone',
        name: 'Salt Stone',
        lore: 'Crystallized Brine Lake mineral. Only found in Pickle Barrels.',
        analog: 'Salt',
        source: 'Pickle Barrel drops only',
        iconKey: 'ingredient_salt_stone',
        color: '#c8c8ff',
    },
};

/** All 4 ingredients are required in qty ≥ 1 each to craft a starter dough */
export const CRAFT_RECIPE: Record<IngredientType, number> = {
    pit_dust:       1,
    brine_drops:    1,
    active_culture: 1,
    salt_stone:     1,
};

// ─────────────────────────────────────────────────────────────────────────────
// OVEN / BAKING SYSTEM
// ─────────────────────────────────────────────────────────────────────────────

export type BakingStageId = 'proofing' | 'rising' | 'baking' | 'complete';

export interface BakingStage {
    id: BakingStageId;
    name: string;
    lore: string;
    /** Interaction verb shown on the action button */
    actionLabel: string;
    /** Flavor copy shown when the window opens */
    windowCopy: string;
    /** Miss penalty description */
    missPenalty: string;
}

export const BAKING_STAGES: Record<BakingStageId, BakingStage> = {
    proofing: {
        id: 'proofing',
        name: 'The Proofing Drawer',
        lore: 'Your dough is resting. Keep it warm.',
        actionLabel: 'Swaddle the dough',
        windowCopy: "Your culture needs to be swaddled. Don't leave it out there.",
        missPenalty: '-1 from socket ceiling roll',
    },
    rising: {
        id: 'rising',
        name: 'The Rise',
        lore: "It's alive. Feed it before it gets ideas.",
        actionLabel: 'Feed the dough',
        windowCopy: 'Drag an ingredient into the rising drawer. The culture is watching.',
        missPenalty: 'Possible rarity downgrade (1 tier)',
    },
    baking: {
        id: 'baking',
        name: 'The Bake',
        lore: 'Into the industrial oven. Do not open the door.',
        actionLabel: '',
        windowCopy: 'The oven is sealed. There is nothing to do but wait.',
        missPenalty: 'None',
    },
    complete: {
        id: 'complete',
        name: 'Bake Complete',
        lore: 'Your baguette is ready. The Lab awaits.',
        actionLabel: 'Claim baguette',
        windowCopy: "Something has been born. It is warm. It has sockets.",
        missPenalty: '',
    },
};

export interface OvenSlot {
    slotId: 'primary' | 'secondary';
    /** null = empty slot */
    activeBake: ActiveBake | null;
}

export interface ActiveBake {
    id: string;
    doughId: string;
    strainId: StarterStrainId;
    rarity: Rarity;
    /** Stage interaction records */
    stages: {
        proofing:  StageRecord;
        rising:    StageRecord;
        baking:    StageRecord;
    };
    /** Timestamps */
    startedAt: number;
    /** When baking stage ends and baguette is claimable */
    completesAt: number;
    /** Current stage */
    currentStage: BakingStageId;
    /** Pre-rolled final socket count — shown only on claim */
    _resolvedSockets: number;
    /** Pre-rolled final rarity — may differ from dough rarity due to neglect */
    _resolvedRarity: Rarity;
    /** Whether the dill blessing socket-boost has been applied to this bake */
    _dillBlessingUsed?: boolean;
}

export interface StageRecord {
    /** When the interaction window opened */
    windowOpenAt: number;
    /** When the interaction window closes (miss if not acted by this time) */
    windowCloseAt: number;
    /** Whether the player acted in time */
    completed: boolean;
    /** When they actually acted (null if missed) */
    completedAt: number | null;
}

// ─────────────────────────────────────────────────────────────────────────────
// TEMP CHECK FLAVOR TEXT (rotating, no mechanical effect)
// ─────────────────────────────────────────────────────────────────────────────

export const OVEN_FLAVOR_TEXT: string[] = [
    'The dough is breathing. LoafPickle compliance officers have been notified.',
    'Temperature nominal. Something smells like victory and sourdough.',
    'Your culture is thriving. This is either great news or a biohazard.',
    'The proofing drawer is warm. The dough has no complaints at this time.',
    'Fermentation readings: elevated. The culture is ambitious.',
    'A faint bubbling. Everything is fine. Please do not call anyone.',
    "The rise is steady. The Pit watches over all yeast cultures.",
    'Structural integrity: holding. Flavor profile: unprecedented.',
    'Your baguette is developing personality. This was not in the manual.',
    "LoafPickle Worldwide LLC cannot be held responsible for what happens next.",
];

// ─────────────────────────────────────────────────────────────────────────────
// BARREL DROP TYPES (baguettes removed, ingredients + stones only)
// ─────────────────────────────────────────────────────────────────────────────

export interface BarrelDrop {
    rarity: BarrelRarity;
    type: 'stone' | 'ingredient' | 'starter_dough';
    stone?: Stone;
    ingredient?: IngredientType;
    starterDough?: StarterDough;
}

// ─────────────────────────────────────────────────────────────────────────────
// RARITY ODDS — by player level band
// ─────────────────────────────────────────────────────────────────────────────

const RARITY_WEIGHTS: Record<string, Record<BarrelRarity, number>> = {
    low:   { fresh: 70, brined: 22, fermented: 7,  legendary_dill: 1  }, // lv 1–14
    mid:   { fresh: 50, brined: 32, fermented: 14, legendary_dill: 4  }, // lv 15–29
    high:  { fresh: 30, brined: 35, fermented: 27, legendary_dill: 8  }, // lv 30–49
    elite: { fresh: 15, brined: 30, fermented: 38, legendary_dill: 17 }, // lv 50+
};

function _levelBand(level: number): string {
    if (level < 15) return 'low';
    if (level < 30) return 'mid';
    if (level < 50) return 'high';
    return 'elite';
}

export function _weightedPick<T extends string>(weights: Record<T, number>): T {
    const total = Object.values(weights).reduce((s: number, w) => s + (w as number), 0);
    let r = Math.random() * total;
    for (const [key, w] of Object.entries(weights)) {
        r -= w as number;
        if (r <= 0) return key as T;
    }
    return Object.keys(weights)[0] as T;
}

// ─────────────────────────────────────────────────────────────────────────────
// STONE VALUE ROLL — weighted toward lower end of range
// ─────────────────────────────────────────────────────────────────────────────

const STONE_RANGES: Record<Rarity, { min: number; max: number; weights: number[] }> = {
    common:     { min: 1, max: 4,  weights: [30, 28, 25, 17] },
    uncommon:   { min: 3, max: 6,  weights: [30, 28, 24, 18] },
    rare:       { min: 5, max: 8,  weights: [30, 27, 25, 18] },
    ultra_rare: { min: 6, max: 9,  weights: [32, 28, 24, 16] },
    legendary:  { min: 7, max: 10, weights: [34, 30, 24, 12] },
};

function _rollStoneValue(rarity: Rarity): number {
    const { min, weights } = STONE_RANGES[rarity];
    const total = weights.reduce((s, w) => s + w, 0);
    let r = Math.random() * total;
    for (let i = 0; i < weights.length; i++) {
        r -= weights[i];
        if (r <= 0) return min + i;
    }
    return min;
}

// ─────────────────────────────────────────────────────────────────────────────
// STONE META — SVG icon keys replace emojis
// ─────────────────────────────────────────────────────────────────────────────

export interface StoneMeta {
    label: string;
    iconKey: string;
    color: string;
    statKey: string;
    mode: StoneMode;
    minRarity: Rarity;
}

export const STONE_META: Record<StoneType, StoneMeta> = {
    // ── Adventure ──────────────────────────────────────────────────────────
    power:    { label: 'Power Stone',    iconKey: 'stone_power',    color: '#ff8800', statKey: 'Crumb cannon dmg',     mode: 'adventure', minRarity: 'common'     },
    chaos:    { label: 'Chaos Stone',    iconKey: 'stone_chaos',    color: '#cc44ff', statKey: 'Ball damage',          mode: 'adventure', minRarity: 'common'     },
    brine:    { label: 'Brine Stone',    iconKey: 'stone_brine',    color: '#00e5ff', statKey: 'Shield strength',      mode: 'adventure', minRarity: 'common'     },
    crust:    { label: 'Crust Stone',    iconKey: 'stone_crust',    color: '#a87a50', statKey: 'Damage resistance',    mode: 'adventure', minRarity: 'uncommon'   },
    // ── Both ───────────────────────────────────────────────────────────────
    dill:     { label: 'Dill Stone',     iconKey: 'stone_dill',     color: '#39ff14', statKey: 'Charge / Cooldown',   mode: 'both',      minRarity: 'uncommon'   },
    score:    { label: 'Score Stone',    iconKey: 'stone_score',    color: '#ffd700', statKey: 'Score multiplier',    mode: 'both',      minRarity: 'common'     },
    ferment:  { label: 'Ferment Stone',  iconKey: 'stone_ferment',  color: '#b8ff00', statKey: 'XP & Credit gain',    mode: 'both',      minRarity: 'rare'       },
    void:     { label: 'Void Stone',     iconKey: 'stone_void',     color: '#6633cc', statKey: 'Random effect',       mode: 'both',      minRarity: 'ultra_rare' },
    eternal:  { label: 'Eternal Stone',  iconKey: 'stone_eternal',  color: '#ffffff', statKey: 'Amplify all stones',  mode: 'both',      minRarity: 'legendary'  },
    // ── PvP ────────────────────────────────────────────────────────────────
    velocity: { label: 'Velocity Stone', iconKey: 'stone_velocity', color: '#ff3366', statKey: 'Pickle speed',        mode: 'pvp',       minRarity: 'common'     },
    topspin:  { label: 'Topspin Stone',  iconKey: 'stone_topspin',  color: '#ff9900', statKey: 'Spin intensity',      mode: 'pvp',       minRarity: 'uncommon'   },
    charge:   { label: 'Charge Stone',   iconKey: 'stone_charge',   color: '#ffcc00', statKey: 'Charge hit power',    mode: 'pvp',       minRarity: 'uncommon'   },
    reflex:   { label: 'Reflex Stone',   iconKey: 'stone_reflex',   color: '#00ffcc', statKey: 'Paddle reaction spd', mode: 'pvp',       minRarity: 'rare'       },
    ghost:    { label: 'Ghost Stone',    iconKey: 'stone_ghost',    color: '#aaaaff', statKey: 'Ball invisibility',   mode: 'pvp',       minRarity: 'ultra_rare' },
};

// ─────────────────────────────────────────────────────────────────────────────
// STONE DROP POOL — which stone types can drop at each barrel rarity
// Ultra Rare / Legendary stones never drop from fresh barrels.
// Higher rarities have small upward-bleed chances.
// ─────────────────────────────────────────────────────────────────────────────

/** Stone types eligible to drop per barrel rarity */
const STONE_DROP_POOL: Record<BarrelRarity, StoneType[]> = {
    fresh:          ['power', 'chaos', 'brine', 'score', 'velocity'],
    brined:         ['power', 'chaos', 'brine', 'score', 'velocity', 'dill', 'crust', 'topspin', 'charge'],
    fermented:      ['power', 'chaos', 'brine', 'score', 'velocity', 'dill', 'crust', 'topspin', 'charge', 'ferment', 'reflex'],
    legendary_dill: ['power', 'chaos', 'brine', 'score', 'velocity', 'dill', 'crust', 'topspin', 'charge', 'ferment', 'reflex', 'void', 'ghost', 'eternal'],
};

/**
 * Stone rarity is typically equal to barrel rarity,
 * with small upward-bleed for mid-tier+ barrels.
 */
function _rollStoneRarity(barrelRarity: BarrelRarity): Rarity {
    const base = BARREL_TO_RARITY[barrelRarity];
    // Upward bleed — small chance to get the next tier up
    const bleed = Math.random();
    if (barrelRarity === 'brined'   && bleed < 0.05) return 'rare';
    if (barrelRarity === 'fermented' && bleed < 0.08) return 'ultra_rare';
    if (barrelRarity === 'legendary_dill' && bleed < 0.10) return 'legendary';
    return base;
}

function _generateStone(barrelRarity: BarrelRarity): Stone {
    const pool = STONE_DROP_POOL[barrelRarity];
    const type = pool[Math.floor(Math.random() * pool.length)] as StoneType;
    const stoneRarity = _rollStoneRarity(barrelRarity);
    const value = _rollStoneValue(stoneRarity);
    const meta = STONE_META[type];
    return {
        id: `stone_${type}_${Date.now()}_${Math.floor(Math.random() * 9999)}`,
        type,
        rarity: stoneRarity,
        value,
        label: `${meta.label} +${value}%`,
        iconKey: meta.iconKey,
        color: meta.color,
        mode: meta.mode,
        minBaguetteRarity: meta.minRarity,
    };
}

// ─────────────────────────────────────────────────────────────────────────────
// BARREL DROP TYPE WEIGHTS
// Stones: 70%, Ingredients: 25%, Rare full starter dough: 5% (common only)
// Salt Stone is an ingredient — always drops via ingredient channel
// ─────────────────────────────────────────────────────────────────────────────

function _rollDropType(barrelRarity: BarrelRarity): 'stone' | 'ingredient' | 'starter_dough' {
    const r = Math.random();
    // Legendary barrels have a 5% chance to drop a Common starter dough
    if (barrelRarity === 'legendary_dill' && r < 0.05) return 'starter_dough';
    if (r < 0.70) return 'stone';
    return 'ingredient';
}

function _rollIngredient(barrelRarity: BarrelRarity): IngredientType {
    // Salt Stone only drops from brined+ barrels
    const pool: IngredientType[] =
        barrelRarity === 'fresh'
            ? ['pit_dust', 'brine_drops', 'active_culture']
            : ['pit_dust', 'brine_drops', 'active_culture', 'salt_stone'];
    return pool[Math.floor(Math.random() * pool.length)];
}

function _generateStarterDough(source: StarterDough['source']): StarterDough {
    return {
        id: `dough_${Date.now()}_${Math.floor(Math.random() * 9999)}`,
        strainId: 'pit_culture',
        rarity: 'common',
        source,
        acquiredAt: Date.now(),
        tradeable: false,
    };
}

// ─────────────────────────────────────────────────────────────────────────────
// GACHA PULL — Marketplace / Credit purchase
// ─────────────────────────────────────────────────────────────────────────────

const GACHA_WEIGHTS: Record<StarterStrainId, number> = {
    pit_culture:      45,
    brine_wild_yeast: 35,
    fermented_chaos:  12,
    ancient_dill_spore: 7,
    eternal_crumb:    1,
};

/**
 * Roll a gacha pull for a starter dough.
 * Used by both the USDC marketplace and Brine Credit purchases.
 */
export function rollStarterDoughGacha(): StarterDough {
    const strainId = _weightedPick(GACHA_WEIGHTS);
    const strain = STARTER_STRAINS[strainId];
    return {
        id: `dough_${Date.now()}_${Math.floor(Math.random() * 9999)}`,
        strainId,
        rarity: strain.rarity,
        source: 'gacha',
        acquiredAt: Date.now(),
        tradeable: false,
    };
}

// ─────────────────────────────────────────────────────────────────────────────
// BAKE RESOLUTION — called server-side / at claim time
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Resolves the final socket count and rarity for a completed bake.
 * Penalties apply for missed stage interactions.
 */
export function resolveBake(
    strainId: StarterStrainId,
    missedProofing: boolean,
    missedRising: boolean,
): { resolvedSockets: number; resolvedRarity: Rarity } {
    const strain = STARTER_STRAINS[strainId];
    let sockets = strain.socketCeiling;
    let rarity = strain.rarity;

    if (missedProofing) sockets = Math.max(1, sockets - 1);

    if (missedRising) {
        const rarityOrder: Rarity[] = ['common', 'uncommon', 'rare', 'ultra_rare', 'legendary'];
        const idx = rarityOrder.indexOf(rarity);
        if (idx > 0) rarity = rarityOrder[idx - 1];
    }

    return { resolvedSockets: sockets, resolvedRarity: rarity };
}

// ─────────────────────────────────────────────────────────────────────────────
// STONE MERGE — Level 15 unlock
// ─────────────────────────────────────────────────────────────────────────────

const MERGE_OUTPUT_POOL: Record<Rarity, StoneType[]> = {
    common:     ['power', 'chaos', 'brine', 'score', 'velocity'],
    uncommon:   ['power', 'chaos', 'brine', 'score', 'velocity', 'dill', 'crust', 'topspin', 'charge'],
    rare:       ['power', 'chaos', 'brine', 'score', 'velocity', 'dill', 'crust', 'topspin', 'charge', 'ferment', 'reflex'],
    ultra_rare: ['power', 'chaos', 'brine', 'score', 'velocity', 'dill', 'crust', 'topspin', 'charge', 'ferment', 'reflex', 'void', 'ghost'],
    legendary:  ['eternal', 'void', 'ghost', 'ferment', 'dill'],
};

const MERGE_RARITY_UP: Record<Rarity, Rarity | null> = {
    common:     'uncommon',
    uncommon:   'rare',
    rare:       'ultra_rare',
    ultra_rare: 'legendary',
    legendary:  null,
};

/**
 * Merge 5 stones of the same rarity → 1 random stone of the next rarity.
 * Returns null if rarity is already legendary (can't merge up).
 */
export function mergeStones(inputRarity: Rarity): Stone | null {
    const outputRarity = MERGE_RARITY_UP[inputRarity];
    if (!outputRarity) return null;
    const pool = MERGE_OUTPUT_POOL[outputRarity];
    const type = pool[Math.floor(Math.random() * pool.length)] as StoneType;
    const value = _rollStoneValue(outputRarity);
    const meta = STONE_META[type];
    return {
        id: `stone_${type}_${Date.now()}_${Math.floor(Math.random() * 9999)}`,
        type,
        rarity: outputRarity,
        value,
        label: `${meta.label} +${value}%`,
        iconKey: meta.iconKey,
        color: meta.color,
        mode: meta.mode,
        minBaguetteRarity: meta.minRarity,
    };
}

// ─────────────────────────────────────────────────────────────────────────────
// SOCKET COMPATIBILITY CHECK
// ─────────────────────────────────────────────────────────────────────────────

const RARITY_ORDER: Rarity[] = ['common', 'uncommon', 'rare', 'ultra_rare', 'legendary'];

/**
 * Returns true if a stone can be socketed into a baguette.
 * Rule: stone.minBaguetteRarity ≤ baguette.rarity
 * (lower rarity stones always fit higher rarity baguettes)
 */
export function canSocket(stone: Stone, baguette: Baguette): boolean {
    return RARITY_ORDER.indexOf(stone.minBaguetteRarity) <= RARITY_ORDER.indexOf(baguette.rarity);
}

/**
 * Apply a stone to the first available empty socket in a baguette.
 * Returns updated baguette or null if no eligible socket available.
 */
export function socketStone(baguette: Baguette, stone: Stone): Baguette | null {
    if (!canSocket(stone, baguette)) return null;
    const stones = Array.isArray(baguette.stones) ? baguette.stones : Object.values(baguette.stones || {});
    const emptyIdx = stones.findIndex(s => s === null);
    if (emptyIdx === -1) return null;
    const updated = { ...baguette, stones: [...stones] as (Stone | null)[] };
    updated.stones[emptyIdx] = stone;
    return updated;
}

/**
 * Remove a stone from a specific socket index.
 * Returns { baguette, stone } or null if socket was empty.
 */
export function unsocketStone(baguette: Baguette, socketIndex: number): { baguette: Baguette; stone: Stone } | null {
    const stones = Array.isArray(baguette.stones) ? baguette.stones : Object.values(baguette.stones || {});
    const stone = stones[socketIndex] as Stone | null;
    if (!stone) return null;
    const updated = { ...baguette, stones: [...stones] as (Stone | null)[] };
    updated.stones[socketIndex] = null;
    return { baguette: updated, stone };
}

/**
 * Compute aggregate stat bonuses from all socketed stones.
 * Returns a map of statKey → total % bonus.
 */
export function computeBaguetteStats(baguette: Baguette): Record<string, number> {
    const totals: Record<string, number> = {};
    const stones = (Array.isArray(baguette.stones) 
        ? baguette.stones 
        : Object.values(baguette.stones || {})) as (Stone | null)[];

    for (const stone of stones) {
        if (!stone) continue;
        const key = STONE_META[stone.type].statKey;
        totals[key] = (totals[key] || 0) + stone.value;
    }
    return totals;
}

// ─────────────────────────────────────────────────────────────────────────────
// PUBLIC API — Barrel Rolling
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Roll a Pickle Barrel drop for a player at a given level.
 * Drops stones (70%), ingredients (25%), or a rare Common starter dough (5% from legendary).
 */
export function rollPickleBarrel(playerLevel: number): BarrelDrop {
    const band = _levelBand(playerLevel);
    const rarity = _weightedPick(RARITY_WEIGHTS[band]);
    const dropType = _rollDropType(rarity);

    if (dropType === 'stone') {
        return { rarity, type: 'stone', stone: _generateStone(rarity) };
    }
    if (dropType === 'starter_dough') {
        return { rarity, type: 'starter_dough', starterDough: _generateStarterDough('gift') };
    }
    return { rarity, type: 'ingredient', ingredient: _rollIngredient(rarity) };
}

// ─────────────────────────────────────────────────────────────────────────────
// DISPLAY HELPERS
// ─────────────────────────────────────────────────────────────────────────────

/** Human-readable barrel rarity label */
export function rarityLabel(rarity: BarrelRarity): string {
    return {
        fresh:          'Fresh',
        brined:         'Brined',
        fermented:      'Fermented',
        legendary_dill: 'Legendary Dill',
    }[rarity];
}

/** Human-readable unified rarity label */
export function rarityDisplayLabel(rarity: Rarity): string {
    return {
        common:     'Common',
        uncommon:   'Uncommon',
        rare:       'Rare',
        ultra_rare: 'Ultra Rare',
        legendary:  'Legendary',
    }[rarity];
}

/** Neon-industrial color styles per BarrelRarity — unchanged for barrel UI */
export const RARITY_STYLES: Record<BarrelRarity, { bg: string; border: string; text: string; glow: string }> = {
    fresh:          { bg: '#EAF3DE', border: '#639922', text: '#27500A', glow: 'rgba(99,153,34,0.3)'   },
    brined:         { bg: '#E1F5EE', border: '#1D9E75', text: '#085041', glow: 'rgba(29,158,117,0.3)'  },
    fermented:      { bg: '#FAEEDA', border: '#BA7517', text: '#633806', glow: 'rgba(186,117,23,0.3)'  },
    legendary_dill: { bg: '#EEEDFE', border: '#7F77DD', text: '#3C3489', glow: 'rgba(127,119,221,0.4)' },
};

/** Dark-mode neon styles for the unified Rarity system (baguettes, stones, doughs) */
export const RARITY_NEON: Record<Rarity, { border: string; text: string; glow: string; bg: string }> = {
    common:     { border: '#639922', text: '#a3e635', glow: 'rgba(99,153,34,0.35)',    bg: 'rgba(99,153,34,0.08)'    },
    uncommon:   { border: '#1D9E75', text: '#34d399', glow: 'rgba(29,158,117,0.35)',   bg: 'rgba(29,158,117,0.08)'   },
    rare:       { border: '#BA7517', text: '#fbbf24', glow: 'rgba(186,117,23,0.35)',   bg: 'rgba(186,117,23,0.08)'   },
    ultra_rare: { border: '#cc44ff', text: '#e879f9', glow: 'rgba(204,68,255,0.35)',   bg: 'rgba(204,68,255,0.08)'   },
    legendary:  { border: '#7F77DD', text: '#a78bfa', glow: 'rgba(127,119,221,0.45)', bg: 'rgba(127,119,221,0.10)'  },
};

/** Brine Credit cost per starter strain (direct purchase) */
export const STARTER_CREDIT_COST: Record<StarterStrainId, number> = {
    pit_culture:        2_500,
    brine_wild_yeast:   5_000,
    fermented_chaos:    15_000,
    ancient_dill_spore: 40_000,
    eternal_crumb:      100_000,
};

// ─────────────────────────────────────────────────────────────────────────────
// UNIFIED MARKETPLACE PRICING
// ─────────────────────────────────────────────────────────────────────────────

/** Brine Credits per USDC/USD */
export const CREDITS_PER_USDC = 5_000;

/** Starter Dough Gacha (1 pull) */
export const GACHA_USDC_COST = 1.00;
export const GACHA_CREDIT_COST = 5_000;

/** Laboratory: Extra Yeast (1 unit) */
export const EXTRA_YEAST_USDC_COST = 1.00;
export const EXTRA_YEAST_CREDIT_COST = 5_000;

/** Laboratory: Extra Lives (Future Use) */
export const EXTRA_LIVES_USDC_COST = 2.00;
export const EXTRA_LIVES_CREDIT_COST = 10_000;

/** Oven: Secondary Slot Unlock (Permanent) */
export const SECOND_OVEN_USDC_COST = 20.00;
export const SECOND_OVEN_CREDIT_COST = 100_000;

/** Tournament: Private Event (One-time) */
export const PRIVATE_TOURNAMENT_USDC_COST = 4.99;
export const PRIVATE_TOURNAMENT_CREDIT_COST = 25_000;

/** Bulk Credit Packs (USD/USDC) */
export const CREDIT_PACKS = [
    { id: 'credits_starter', price: 5.00,  label: 'Starter Pack', credits: 25_000,  bonus: 0 },
    { id: 'credits_pro',     price: 10.00, label: 'Pro Pack',     credits: 60_000,  bonus: 10_000 },
    { id: 'credits_elite',   price: 25.00, label: 'Elite Pack',   credits: 175_000, bonus: 50_000 },
];
